/**
 * Preenche placeholders de modelos WhatsApp (Planos → Mensagens).
 */

/** Remove escapes comuns em placeholders ({info\_channel} → {info_channel}). */
export function normalizeAccessTemplate(raw) {
  return String(raw || '').replace(/\{([^{}]+)\}/g, (_, inner) =>
    `{${String(inner).replace(/\\_/g, '_')}}`,
  );
}

/** Monta URL M3U a partir do DNS + credenciais quando a API não devolve o link. */
export function ensureM3uUrl({ m3u, dns, username, password } = {}) {
  const existing = String(m3u || '').trim();
  if (existing && !/sem dns/i.test(existing) && /get\.php|m3u/i.test(existing)) {
    return existing;
  }
  let d = String(dns || '')
    .trim()
    .replace(/\/+$/, '');
  if (!d || /sem dns/i.test(d)) return existing && !/sem dns/i.test(existing) ? existing : '';
  if (!/^https?:\/\//i.test(d)) d = `http://${d}`;
  const u = String(username || '').trim();
  const p = String(password || '').trim();
  if (!u) return '';
  return `${d}/get.php?username=${encodeURIComponent(u)}&password=${encodeURIComponent(p)}&type=m3u_plus&output=ts`;
}

/** Modelo oficial — cliente final. */
export const DEFAULT_CLIENT_ACCESS_TEMPLATE = `✅ *ACESSO LIBERADO COM SUCESSO!*

Olá! Seu plano já está ativo e disponível. Confira todas as informações abaixo:

━━━━━━━━━━━━━━
🔐 *DADOS DE ACESSO*

👤 *Usuário:* {username}
🔒 *Senha:* {password}
🌐 *DNS/URL:* {dns}
📺 *Telas simultâneas:* {connections}
📅 *Vencimento:* {expiry}
📦 *Plano contratado:* {package}

━━━━━━━━━━━━━━
📲 *APLICATIVOS DISPONÍVEIS*

{app_block}

━━━━━━━━━━━━━━
🔗 *OUTRAS FORMAS DE ACESSO*

▶️ *Web Player:*
{webplayer}

📋 *Lista M3U:*
{m3u}

━━━━━━━━━━━━━━
🔄 *ASSINATURA E RENOVAÇÃO*

Para renovar seu plano e evitar interrupções, acesse:

🔗 {link_renovacao}

━━━━━━━━━━━━━━
🛟 *ATENDIMENTO E SUPORTE*

Precisa de ajuda para instalar, configurar ou acessar?

📱 *WhatsApp:* {support}

━━━━━━━━━━━━━━
📢 *CANAL OFICIAL DE INFORMAÇÕES*

Acompanhe *avisos, novidades e atualizações importantes:*

🔗 {info_channel}

━━━━━━━━━━━━━━
⚠️ *IMPORTANTE*

Não compartilhe seu *usuário, senha, Lista M3U ou Web Player*. Utilize somente a quantidade de telas contratadas e, para evitar interrupções, renove antes do vencimento.

💙 *Agradecemos pela preferência. Aproveite seu acesso!*`;

/** Modelo oficial — teste rápido. */
export const DEFAULT_TRIAL_ACCESS_TEMPLATE = `⚡ *TESTE RÁPIDO LIBERADO!*

Olá! Seu acesso de demonstração foi gerado com sucesso. Confira abaixo:

━━━━━━━━━━━━━━
🔐 *DADOS DO TESTE*

👤 *Usuário:* {username}
🔒 *Senha:* {password}
🌐 *DNS/URL:* {dns}
📦 *Plano:* {package}
⏳ *Válido até:* {expiry}

━━━━━━━━━━━━━━
📲 *APLICATIVOS DISPONÍVEIS*

{app_block}

━━━━━━━━━━━━━━
🔗 *OUTRAS FORMAS DE ACESSO*

▶️ *Web Player:*
{webplayer}

📋 *Lista M3U:*
{m3u}

━━━━━━━━━━━━━━
🛟 *ATENDIMENTO E SUPORTE*

Precisa de ajuda para instalar ou configurar?

📱 *WhatsApp:* {support}

━━━━━━━━━━━━━━
📢 *CANAL OFICIAL DE INFORMAÇÕES*

Acompanhe *avisos, novidades e atualizações importantes:*

🔗 {info_channel}

━━━━━━━━━━━━━━
⚠️ Este acesso é *temporário*. Faça o teste antes do vencimento e não compartilhe seu *usuário, senha, Lista M3U ou Web Player*.

💙 *Aproveite a experiência! Nossa equipe está à disposição.*`;

export function fillAccessTemplate(raw, vars = {}) {
  let out = normalizeAccessTemplate(raw);
  if (!out.trim()) return '';
  const renewal = vars.link_renovacao ?? vars.link_pagamento ?? '';
  const webplayer =
    vars.webplayer ??
    vars.webplayer_url ??
    vars.player ??
    vars.web_player ??
    '';
  const price = vars.plan_price ?? vars.valor ?? vars.price ?? '';
  const support =
    vars.support ??
    vars.suporte ??
    vars.support_phone ??
    vars.support_whatsapp ??
    '';
  const supportDigits = String(support).replace(/\D/g, '') || String(support || '');
  const supportLink =
    vars.support_link ||
    (String(supportDigits).replace(/\D/g, '').length >= 10
      ? `https://wa.me/${String(supportDigits).replace(/\D/g, '')}`
      : '');
  const infoChannel = String(
    vars.info_channel ??
      vars.info_channel_url ??
      vars.canal_info ??
      vars.canal_informacoes ??
      '',
  ).trim();
  let appBlock = String(vars.app_block ?? vars.aplicativo ?? vars.partner_app ?? '').trim();
  if (webplayer && appBlock.includes('{webplayer}')) {
    appBlock = appBlock.split('{webplayer}').join(webplayer);
  }
  const m3u = ensureM3uUrl({
    m3u: vars.m3u,
    dns: vars.dns ?? vars.dns_label,
    username: vars.username,
    password: vars.password,
  });
  const map = {
    '{username}': vars.username ?? '',
    '{password}': vars.password ?? '',
    '{dns}': vars.dns ?? vars.dns_label ?? '',
    '{expiry}': vars.expiry ?? vars.exp_date_formatted ?? '',
    '{connections}': vars.connections ?? '',
    '{package}': vars.package ?? vars.package_name ?? vars.display_name ?? '',
    '{whatsapp}': vars.whatsapp ?? '',
    '{notes}': vars.notes ?? '',
    '{m3u}': m3u,
    '{m3u_url}': m3u,
    '{link_renovacao}': renewal,
    '{link_pagamento}': renewal,
    '{webplayer}': webplayer,
    '{player}': webplayer,
    '{web_player}': webplayer,
    '{plan_price}': price,
    '{valor}': price,
    '{price}': price,
    '{support}': supportDigits,
    '{suporte}': supportDigits,
    '{support_phone}': supportDigits,
    '{support_whatsapp}': supportDigits,
    '{support_link}': supportLink,
    '{info_channel}': infoChannel,
    '{info_channel_url}': infoChannel,
    '{canal_info}': infoChannel,
    '{canal_informacoes}': infoChannel,
    '{app_block}': appBlock,
    '{aplicativo}': appBlock,
    '{partner_app}': appBlock,
  };
  for (const [k, v] of Object.entries(map)) {
    out = out.split(k).join(String(v ?? ''));
  }
  // Não exiba blocos promocionais/configuráveis vazios. Além de deixar a
  // mensagem com aparência inacabada, um rótulo sem URL pode confundir o
  // cliente. O bloco M3U permanece quando disponível.
  if (!webplayer) {
    out = out.replace(/▶️\s*\*?Web Player:?\*?\s*\r?\n\s*/gi, '');
  }
  const removeSection = (text, heading) => text.replace(
    new RegExp(`\\r?\\n━━━━━━━━━━━━━━\\r?\\n${heading}[\\s\\S]*?(?=\\r?\\n━━━━━━━━━━━━━━\\r?\\n|$)`, 'gi'),
    '',
  );
  if (!renewal) out = removeSection(out, '🔄\\s*\\*?ASSINATURA E RENOVAÇÃO\\*?');
  if (!supportDigits) out = removeSection(out, '🛟\\s*\\*?ATENDIMENTO E SUPORTE\\*?');
  if (!infoChannel) out = removeSection(out, '📢\\s*\\*?CANAL OFICIAL DE INFORMAÇÕES\\*?');
  return out;
}

/** Injeta a URL M3U se a secção «Lista M3U» veio vazia na mensagem já renderizada. */
export function patchMissingM3uInMessage(msg, vars = {}) {
  const text = String(msg || '');
  if (!text) return text;
  const m3u = ensureM3uUrl({
    m3u: vars.m3u,
    dns: vars.dns ?? vars.dns_label,
    username: vars.username,
    password: vars.password,
  });
  if (!m3u) return text;
  return text.replace(
    /(📋\s*\*?Lista M3U:?\*?\s*\r?\n)([^\r\n]*)/i,
    (full, label, line) => {
      const cur = String(line || '').trim();
      if (cur && /get\.php|m3u_plus|type=m3u|https?:\/\//i.test(cur) && !/sem dns/i.test(cur)) {
        return full;
      }
      return `${label}${m3u}`;
    },
  );
}

/**
 * Monta a mensagem final sem duplicar o bloco do app.
 * Prioridade: mensagem resolvida pela API → template legado do plano → fallback local.
 */
export function composeAccessMessage({
  apiTemplate = '',
  planTplRaw = '',
  defaultTpl = '',
  vars = {},
} = {}) {
  const fromApi = String(apiTemplate || '').trim();
  const planRaw = String(planTplRaw || '').trim();
  const fallback = String(defaultTpl || '').trim();

  // The server resolved the exact plan, Office settings and selected application.
  // Do not replace that rendered copy with a generic or stale browser template.
  if (fromApi && !isGenericLineTemplate(fromApi)) {
    return fromApi;
  }
  if (planRaw) {
    return patchMissingM3uInMessage(fillAccessTemplate(planRaw, vars), vars);
  }
  // Compatibility fallback only when the API did not provide a configured message.
  if (fallback) {
    return patchMissingM3uInMessage(fillAccessTemplate(fallback, vars), vars);
  }
  return patchMissingM3uInMessage(fromApi, vars);
}

/** Detecta o fallback genérico do backend (quando o template do plano não foi encontrado). */
export function isGenericLineTemplate(msg) {
  const s = String(msg || '').trim();
  if (!s) return true;
  return (
    /^✅\s*Linha Gerada!?/i.test(s) &&
    /Usuário:/i.test(s) &&
    /Senha:/i.test(s) &&
    !/\{[a-z_]+\}/i.test(s) &&
    s.split('\n').length <= 6
  );
}

/**
 * Escolhe o texto do plano: teste rápido → quick; cliente → longo.
 * Se o preferido estiver vazio, usa o outro.
 */
export function pickPlanTemplate(tmplDoc, { isTrial = false } = {}) {
  if (!tmplDoc || typeof tmplDoc !== 'object') return '';
  const longTpl = String(tmplDoc.template || '').trim();
  const quickTpl = String(tmplDoc.quick_test_template || '').trim();
  if (isTrial) return quickTpl || longTpl;
  return longTpl || quickTpl;
}
